import { AUDIO_BALANCE_WAVEFORM } from "@/consts/waveforms";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

type WaveformPlayerProps = {
  audioRef: React.RefObject<HTMLAudioElement>;
  className?: string;
  title: "Before" | "After";
};

const WaveformPlayer = ({ audioRef, className, title }: WaveformPlayerProps) => {
  const [progressPercent, setProgressPercent] = useState(0);
  const waveformRef = useRef<HTMLDivElement>(null);

  const updateProgress = useCallback(() => {
    if (!audioRef.current) return;

    const percent = audioRef.current.currentTime / audioRef.current.duration;
    setProgressPercent(percent);
  }, [audioRef]);

  const seekAudio = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !waveformRef.current) return;

    const waveformContainer = e.currentTarget;
    const clickPosition = e.clientX - waveformContainer.getBoundingClientRect().left;
    const percent = clickPosition / waveformContainer.offsetWidth;

    audioRef.current.currentTime = percent * audioRef.current.duration;
    updateProgress();
  };

  const handleScrubbing = useCallback(() => {
    let isDragging = false;

    const startDrag = (e: MouseEvent) => {
      isDragging = true;
      document.body.style.userSelect = "none";
    };

    const drag = (e: MouseEvent) => {
      if (!isDragging || !audioRef.current || !waveformRef.current) return;

      const waveformContainer = waveformRef.current;
      const rect = waveformContainer.getBoundingClientRect();

      let position = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const percent = position / rect.width;

      audioRef.current.currentTime = percent * audioRef.current.duration;
      updateProgress();
    };

    const endDrag = () => {
      isDragging = false;
      document.body.style.userSelect = "";
    };

    if (waveformRef.current) {
      waveformRef.current.addEventListener("mousedown", startDrag);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", endDrag);
    }

    return () => {
      if (waveformRef.current) {
        waveformRef.current.removeEventListener("mousedown", startDrag);
      }
      document.removeEventListener("mousemove", drag);
      document.removeEventListener("mouseup", endDrag);
    };
  }, [audioRef, updateProgress]);

  useEffect(() => {
    const cleanup = handleScrubbing();
    return cleanup;
  }, [handleScrubbing]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      updateProgress();
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [updateProgress, audioRef]);

  return (
    <div
      className={cn(
        "relative flex h-12 w-full cursor-pointer items-center justify-between gap-2 overflow-hidden py-2",
        className,
      )}
    >
      <div className="flex h-full w-full flex-col gap-1">
        <div className="flex h-1/2 w-full items-center gap-1">
          <p className="w-2 text-sm text-gray-600">L</p>
          <div
            ref={waveformRef}
            className="flex h-full w-full items-center justify-center"
            onClick={seekAudio}
          >
            {AUDIO_BALANCE_WAVEFORM[title].left.map((leftValue, index) => {
              const sampleProgress = index / AUDIO_BALANCE_WAVEFORM[title].left.length;
              const isActive = sampleProgress < progressPercent;

              return (
                <div
                  key={`left-${index}`}
                  className="flex h-full min-w-0 flex-1 items-center justify-center"
                >
                  <div
                    className="w-full transition-colors duration-100"
                    style={{
                      height: `${leftValue ? leftValue * 100 : 2}%`,
                      backgroundColor: isActive ? "#0052B4" : "#DBDBDB",
                      marginLeft: "0.5px",
                      marginRight: "0.5px",
                      borderRadius: "3px",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex h-1/2 w-full items-center gap-1">
          <p className="w-2 text-sm text-gray-600">R</p>
          <div className="flex h-full w-full items-center justify-center" onClick={seekAudio}>
            {AUDIO_BALANCE_WAVEFORM[title].right.map((rightValue, index) => {
              const sampleProgress = index / AUDIO_BALANCE_WAVEFORM[title].right.length;
              const isActive = sampleProgress < progressPercent;

              return (
                <div
                  key={`right-${index}`}
                  className="flex h-full min-w-0 flex-1 items-center justify-center"
                >
                  <div
                    className="w-full transition-colors duration-100"
                    style={{
                      height: `${rightValue ? rightValue * 100 : 2}%`,
                      backgroundColor: isActive ? "#0052B4" : "#DBDBDB",
                      marginLeft: "0.5px",
                      marginRight: "0.5px",
                      borderRadius: "3px",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaveformPlayer;
